// V2 "Total Demand" cell — renders the open-demand timeline the backend
// (`ShopFloorV2View`) precomputes per output_item_id.
//
// Layout (always-visible, no hover needed):
//   - Prominent "{total} total" header.
//   - Vertical mini-table with one row per time WINDOW: label, the
//     window's own (incremental) quantity in bold, and the cumulative
//     running total by the end of that window dimmed in parentheses:
//     "32 total → Next 3 days 19 (19) · This week 0 (19) ·
//      Next 2 weeks 4 (23) · Next 30 days 6 (29) · Later 3 (32)".
//
// The backend buckets are cumulative by construction
// (total >= this_month >= next_2_weeks >= this_week >= next_3_days; a WO
// due in 2 days contributes to every bucket), so each window is the
// difference between adjacent buckets and the cumulative figure is the
// bucket itself. Overdue demand lands in "next 3 days"; WOs with no due
// date land in "later".

import React from 'react';
import { Box, Typography } from '@mui/material';

const isPositive = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0;
};

const fmt = (v) => {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : String(n);
};

export default function DemandTimelineCellV2({ timeline }) {
  if (!timeline) {
    return (
      <Box>
        <Typography variant="h5" color="text.secondary">-</Typography>
        <Typography variant="caption" color="text.secondary">No demand</Typography>
      </Box>
    );
  }

  const total = timeline.total;
  if (!isPositive(total)) {
    return (
      <Box>
        <Typography variant="h5" color="text.secondary">0</Typography>
        <Typography variant="caption" color="text.secondary">No open demand</Typography>
      </Box>
    );
  }

  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const next3 = num(timeline.next_3_days);
  const week = num(timeline.this_week);
  const twoWeeks = num(timeline.next_2_weeks);
  const month = num(timeline.this_month);
  const later = num(total) - month;

  // ``value`` is the window's own share (differences between adjacent
  // cumulative buckets, clamped at 0 in case a stale payload ever violates
  // the cumulative invariant); ``cum`` is the running total by the end of
  // the window — the backend bucket itself.
  const buckets = [
    { key: 'next_3_days', label: 'Next 3 days', value: next3, cum: next3 },
    { key: 'this_week', label: 'This week', value: Math.max(0, week - next3), cum: week },
    { key: 'next_2_weeks', label: 'Next 2 weeks', value: Math.max(0, twoWeeks - week), cum: twoWeeks },
    { key: 'this_month', label: 'Next 30 days', value: Math.max(0, month - twoWeeks), cum: month },
    { key: 'later', label: 'Later', value: Math.max(0, later), cum: num(total) },
  ];

  return (
    <Box data-testid="demand-timeline-cell">
      <Typography variant="h5" fontWeight="bold" color="primary.main">
        {fmt(total)}{' '}
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 400 }}
        >
          total
        </Typography>
      </Typography>
      <Box
        sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'auto auto 1fr', columnGap: 1, rowGap: 0.15 }}
        data-testid="demand-timeline-chips"
      >
        {buckets
          .filter((b) => b.key !== 'later' || b.value > 0)
          .map((b) => {
          // Bold the window carrying the biggest chunk of the demand.
          const maxVal = Math.max(...buckets.map((x) => x.value));
          const isMax = b.value > 0 && b.value === maxVal;
          return (
            <React.Fragment key={b.key}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}
                data-testid={`demand-label-${b.key}`}
              >
                {b.label}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={isMax ? 600 : 400}
                color={isPositive(b.value) ? 'text.primary' : 'text.disabled'}
                data-testid={`demand-chip-${b.key}`}
              >
                {fmt(b.value)}
              </Typography>
              {/* Running total by the end of this window. */}
              <Typography
                variant="caption"
                color="text.primary"
                title="Running total due by the end of this window"
                data-testid={`demand-cum-${b.key}`}
              >
                ({fmt(b.cum)})
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
